import { useCallback, useEffect, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Box } from '@givebutter/primitives/box';
import { DEFAULT_COLORS } from '@givebutter/primitives/color-picker';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { isAcceptableThemeColor } from '@givebutter/util';
import { updateAccount } from 'api/account';
import { LogoUpload } from 'assets/js/pages/account-settings/account-about/LogoUpload';
import * as Form from 'components/form';
import { Settings } from 'constants/settings';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import IframeResizer from 'iframe-resizer-react';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Account, AccountSetting } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface AccountBrandingSettingsFormValues {
    logoHyperlink: string;
    themeColor: string;
}

const BrandingSettings = () => {
    const { apiUrl } = useAppState();
    const { accountId, settings, email_logo } = useCurrentAccount();
    const { key, forceRender } = useForceRender();
    const { success, error } = useToast();

    const defaultValues = useMemo(() => {
        const themeColor = settings.find(item => item.code === Settings.ThemeColor.Code);
        const logoHyperlink = settings.find(
            item => item.code === Settings.EngageLogoHyperlink.Code,
        );

        return {
            themeColor: themeColor?.value ?? '',
            logoHyperlink: logoHyperlink?.value ?? '',
        };
    }, [settings]);

    const methods = Form.useGivebutterForm<AccountBrandingSettingsFormValues>({ defaultValues });
    const { control, path, handleSubmit } = methods;

    const { mutate: submit, isPending } = useMutation<
        Account,
        GivebutterAxiosError,
        { settings: AccountSetting[] }
    >({
        mutationFn: ({ settings }) => updateAccount(accountId, { settings }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });
            success('Branding settings successfully saved!');
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to update branding settings.');
        },
    });

    const handleFormSubmit = useCallback(
        (values: AccountBrandingSettingsFormValues) => {
            const { logoHyperlink, themeColor } = values;

            const newSettings: AccountSetting[] = [
                {
                    code: Settings.ThemeColor.Code,
                    name: Settings.ThemeColor.Name,
                    value: themeColor.length ? themeColor : null,
                    active: !!themeColor,
                    type: 'string',
                },
                {
                    code: Settings.EngageLogoHyperlink.Code,
                    name: Settings.EngageLogoHyperlink.Name,
                    value: logoHyperlink.length ? logoHyperlink : null,
                    active: !!logoHyperlink,
                    type: 'string',
                },
            ];

            submit({ settings: newSettings });
        },
        [submit],
    );

    useEffect(() => {
        forceRender();
    }, [email_logo, defaultValues.themeColor, defaultValues.logoHyperlink, forceRender]);

    return (
        <Box className="flex flex-col gap-6 px-6 py-4">
            <div className="text-lg font-medium">Branding</div>

            <Form.Provider {...methods}>
                <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-6">
                    <div className="flex flex-col gap-6 xl:flex-row">
                        <Form.Container className="min-w-76 w-auto">
                            <Form.Label name={path('themeColor')}>Email logo</Form.Label>
                            <LogoUpload
                                defaultUrl={email_logo?.url}
                                type="email"
                                key={email_logo?.url}
                            />
                        </Form.Container>
                        <div className="flex flex-grow flex-col items-start gap-6">
                            <Form.Container className="max-w-96">
                                <Form.Label name={path('themeColor')}>Brand color</Form.Label>
                                <Form.UseFormColorPicker
                                    control={control}
                                    name={path('themeColor')}
                                    colors={DEFAULT_COLORS.filter(
                                        color => color.toUpperCase() !== '#FFFFFF',
                                    )}
                                    rules={{
                                        validate: value =>
                                            isAcceptableThemeColor(value)
                                                ? true
                                                : 'Choose a darker color for better readability.',
                                    }}
                                />
                                <Form.Error name={path('themeColor')} />
                            </Form.Container>

                            <Form.Container className="max-w-96">
                                <Form.Label name={path('logoHyperlink')}>
                                    Email logo hyperlink
                                </Form.Label>
                                <p className="text-sm text-gray-500">
                                    Leave this blank to default to your Givebutter landing page.
                                </p>
                                <Form.TextInputRoot control={control} name="logoHyperlink">
                                    <Form.TextInputForm placeholder="example.org" />
                                </Form.TextInputRoot>
                                <Form.Error name={path('logoHyperlink')} />
                            </Form.Container>
                            <Form.SubmitButton isSubmitting={isPending}>Save</Form.SubmitButton>
                        </div>
                    </div>
                </form>
            </Form.Provider>

            <Separator />

            <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">Preview</p>
                <IframeResizer
                    key={key}
                    autoResize={false}
                    className="w-full"
                    src={`${apiUrl}/dashboard/accounts/${accountId}/generic_message_preview`}
                />
            </div>
        </Box>
    );
};

export { BrandingSettings };
